import { useState } from "react";
import { X, ExternalLink } from "lucide-react";
import { Button } from "./button";
import { __ } from "@/i18n";
import webyesPromoBanner from "../../assets/webyes-promo-banner.webp";
import { useBannerVisibility } from "@/hooks/useBannerVisibility";
import { useWidgetStore } from "@/stores/widget-store";
import { updateBanner } from "@/services/widget";
import { WEBYES_SCAN_BASE_URL } from "@/constants/banner-urls";

const PROMO_BANNER_ID = "webyes-promo";
const CONSULTATION_BANNER_ID = "consultation-banner";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const Banner = () => {
    const { isVisible, hide } = useBannerVisibility(PROMO_BANNER_ID);
    const banners = useWidgetStore((state) => state.config.banners || {});
    const [isClosing, setIsClosing] = useState(false);
    const siteUrl = window.cyA11yGlobals.site.url;
    const scanUrl = `${WEBYES_SCAN_BASE_URL}?website_url=${encodeURIComponent(siteUrl)}&utm_medium=banner&utm_source=wordpress_plugin&utm_campaign=accessiyes`;

    if (!isVisible) return null;

    const handleClose = async () => {
        if (isClosing) return;
        setIsClosing(true);
        try {
            // Schedule consultation banner to appear 1 day after promo dismissal,
            // but only if the user hasn't already dismissed consultation in the past
            // (e.g. via the previous flow when consultation was shown first).
            const consultation = banners[CONSULTATION_BANNER_ID];
            const consultationNotDismissed = !consultation || consultation.status !== false;
            if (consultationNotDismissed) {
                await updateBanner(CONSULTATION_BANNER_ID, { status: true, until: Date.now() + ONE_DAY_MS });
            }
            await hide();
        } catch (error) {
            setIsClosing(false);
            console.error("Failed to close banner:", error);
        }
    };

    return (
        <div className="cy:max-w-7xl cy:mx-auto cy:px-4 cy:sm:px-6 cy:lg:px-8 cy:pt-8">
            <div className="cy:relative cy:bg-[#0B66E4] cy:rounded-xl cy:shadow-sm cy:flex cy:flex-col cy:lg:flex-row cy:items-stretch cy:min-h-[266px]"
                style={{
                    backgroundImage: `url(${webyesPromoBanner})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                {/* Close Button */}
                <button
                    onClick={handleClose}
                    disabled={isClosing}
                    className="cy:absolute cy:top-6 cy:right-4 cy:z-50 cy:p-1 cy:text-white/70 cy:hover:text-white cy:hover:bg-white/10 cy:rounded-full cy:transition-colors cy:disabled:opacity-50 cy:disabled:cursor-not-allowed"
                    aria-label={__( 'Dismiss banner', 'accessibility-widget' )}
                >
                    <X size={24} />
                </button>

                {/* Left Content Area */}
                <div className="cy:w-[60%] cy:p-8 cy:flex cy:flex-col cy:relative cy:z-10">
                    <span className="cy:text-white cy:text-[12px]">
                        {__( 'Sponsored recommendation', 'accessibility-widget' )}
                    </span>

                    <h2 className="cy:text-[18px] cy:font-bold cy:text-white!">
                        {__( 'Beyond Widgets. Continue Your Accessibility Journey.', 'accessibility-widget' )}
                    </h2>

                    <p className="cy:text-white cy:pb-4 cy:pr-10 cy:text-[14px] cy:leading-relaxed">
                        {__( 'You have taken the first step towards accessibility. Now level up with deeper audits, ongoing monitoring for accessibility issues, and detailed reporting with AI-assisted solutions.', 'accessibility-widget' )}<br />
                        {__( 'Explore WebYes. One platform, better fixes.', 'accessibility-widget' )}
                    </p>

                    <Button
                        onClick={() => window.open(scanUrl, '_blank', 'noopener,noreferrer')}
                        className="cy:self-start cy:bg-white cy:text-[#0B66E4] cy:hover:bg-white/90 cy:font-bold! cy:px-6 cy:py-5 cy:rounded-md cy:flex cy:items-center cy:gap-2 cy:text-[16px]"
                    >
                        {__( 'Run a scan with WebYes', 'accessibility-widget' )}
                        <ExternalLink size={16} />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Banner;
